import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import ProfileAvatar from "@/ui/Profile_Avatar";
import SettingsOption from "@/pages/settings/components/Settings_Option";
import {
  selectUserProfile,
  selectIsSearching,
} from "@/store/redux/slices/user/user_slice";
import { useNavigate } from "react-router-dom";
import { fetchOrganizationById } from "@/store/redux/slices/kyc/kyc_setup_slice";
import { calculateKycStatus } from "@/utils/kyc_utils";

const SettingsMenu = () => {
  const userProfile = useSelector(selectUserProfile);
  const isSearching = useSelector(selectIsSearching);
  const organizationData = useSelector(
    (state) => state.organization.organizationData
  );
  const lastFetchTime = useSelector(
    (state) => state.organization.lastFetchTime
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [kycStatus, setKycStatus] = useState({
    completionPercentage: 0,
    completedSteps: 0,
    incompleteSteps: 0,
  });
  // Avoid flashing "3" from the empty-org default before the first fetch settles.
  const [hasHydratedKyc, setHasHydratedKyc] = useState(
    () => Boolean(lastFetchTime) || Boolean(organizationData?.length)
  );

  useEffect(() => {
    let cancelled = false;
    dispatch(fetchOrganizationById()).finally(() => {
      if (!cancelled) setHasHydratedKyc(true);
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    setKycStatus(calculateKycStatus(organizationData));
  }, [organizationData]);

  const incompleteBadge =
    hasHydratedKyc && kycStatus.incompleteSteps > 0
      ? kycStatus.incompleteSteps
      : null;

  const settingsOptions = [
    {
      id: "general",
      title: "General Settings",
      description: "Account, Password",
      icon: "SettingsGear",
    },
    {
      id: "organization",
      title: "Manage Organization",
      description: "Name, Avatar, Subscription, and more",
      icon: "SettingsFile",
    },
    {
      id: "verification",
      title: "Account Verification",
      description: "Manage KYC, Security Question...",
      icon: "SettingsFile",
    },
    {
      id: "notifications",
      title: "Notification Preferences",
      description: "Alerts, Tones, Frequency",
      icon: "SettingsBell",
    },
    {
      id: "social",
      title: "Connected Social Accounts",
      description: "Manage Social Media Accounts",
      icon: "SettingsFacebook",
    },
    {
      id: "theme",
      title: "App Theme Settings",
      description: "Dark mode or Light Mode",
      icon: "SettingsBrush",
    },
    {
      id: "support",
      title: "About and Help",
      description: "About App, Help and Support",
      icon: "SettingsEssentials1",
    },
    {
      id: "legal",
      title: "Legal Notice",
      description: "Privacy Policy and Terms of Service",
      icon: "SettingsMessage",
    },
    {
      id: "logout",
      title: "Log out",
      description: "About App, Help and Support",
      icon: "SettingsLogout",
    },
  ];

  const handleOptionClick = (optionId) => {
    navigate(`/settings/${optionId}`);
  };

  return (
    <div className="flex flex-col lg:border-r-2 border-r-[#eee] py-6 pr-3">
      <div className="flex flex-col items-center mb-8 px-4">
        <div className="w-full flex justify-center">
          <ProfileAvatar
            imageUrl={userProfile.profileImage}
            badgeNumber={incompleteBadge}
            completionPercentage={
              hasHydratedKyc ? kycStatus.completionPercentage : 0
            }
          />
        </div>
        <div className="text-center w-full">
          <h2 className="mb-1 font-semibold heading-large text-primary truncate max-w-[220px] mx-auto">
            {isSearching ? "Loading..." : userProfile.fullname}
          </h2>
          <p className="text-gray-500 text-regular">
            {isSearching ? "Loading..." : userProfile.email}
          </p>
        </div>
      </div>

      <div className="space-y-4 px-4">
        {settingsOptions.map((option) => (
          <SettingsOption
            key={option.id}
            option={option}
            onClick={() => handleOptionClick(option.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default SettingsMenu;
